import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import './NewPassword.css';
import { api, ENDPOINTS } from '../../services/apiConfig';


const NewPassword = () => {

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({
        score: 0,
        message: ''
    });
    const navigate = useNavigate();

    // Check password strength
    const checkPasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength({ score: 0, message: '' });
            return;
        }

        let score = 0;
        
        // Length check
        if (password.length >= 8) score += 1;
        if (password.length >= 12) score += 1;
        
        // Character type checks
        if (/[A-Z]/.test(password)) score += 1;
        if (/[a-z]/.test(password)) score += 1;
        if (/[0-9]/.test(password)) score += 1;
        if (/[^A-Za-z0-9]/.test(password)) score += 1;
        
        // Final score from 0-4
        const finalScore = Math.min(Math.floor(score / 2), 4);
        
        // Determine message
        let message = '';
        switch (finalScore) {
            case 0: message = 'Very weak'; break;
            case 1: message = 'Weak'; break;
            case 2: message = 'Fair'; break;
            case 3: message = 'Good'; break;
            case 4: message = 'Strong'; break;
            default: message = '';
        }
        
        setPasswordStrength({ score: finalScore, message });
    };

    const getStrengthColor = () => {
        switch (passwordStrength.score) {
            case 0: return '#ff4d4f';
            case 1: return '#ff4d4f';
            case 2: return '#faad14';
            case 3: return '#52c41a';
            case 4: return '#1890ff';
            default: return '#d9d9d9';
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        checkPasswordStrength(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate passwords
        if (newPassword.length < 8) {
            toast.error(
                <div className="toast-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Password must be at least 8 characters long</span>
                </div>
            );
            return;
        }
        
        if (newPassword !== confirmPassword) {
            toast.error(
                <div className="toast-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Passwords do not match</span>
                </div>
            );
            return;
        }

        const jwtToken = sessionStorage.getItem('token');
        if (!jwtToken) {
            toast.error(
                <div className="toast-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Invalid session. Please try again.</span>
                </div>
            );
            navigate('/forgot-password');
            return;
        }

        setIsSubmitting(true);

        try {
            const data = await api.vendor.post(ENDPOINTS.vendor.newpassword,
                { newPassword },
                { headers: { Authorization: `Bearer ${jwtToken}` } }
            );

            if (data.status === 200) {
                toast.success(
                    <div className="toast-content">
                        <i className="fas fa-check-circle"></i>
                        <span>Password reset successful!</span>
                    </div>
                );
                
                // Clear the JWT token after successful reset
                sessionStorage.removeItem('token');
                
                // Redirect after delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } catch (error) {
            const errorMessage = error?.data?.message || 'Failed to reset password. Please try again.';
            
            toast.error(
                <div className="toast-content">
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>{errorMessage}</span>
                </div>
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='new-password-body'>
            <div className='new-password-container'>
                <div className="logo-wrapper">
                    <img src='/assets/genie.png' alt='Genie Logo' className='new-password-logo' />
                </div>
                <h1>Reset Your Password</h1>
                <p className="password-instruction">Create a strong password to secure your account</p>
                
                <form onSubmit={handleSubmit}>
                    <div className='form-group'>
                        <label htmlFor='newPassword'>
                            <i className="fas fa-lock"></i> New Password
                        </label>
                        <div className="password-input-container">
                            <input
                                type={showNewPassword ? 'text' : 'password'}
                                id='newPassword'
                                placeholder='Enter new password'
                                value={newPassword}
                                onChange={handlePasswordChange}
                                required
                            />
                            <button 
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                            >
                                <i className={showNewPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                            </button>
                        </div>
                        
                        {newPassword && (
                            <div className="password-strength-container">
                                <div className="strength-bars">
                                    {[0, 1, 2, 3].map((index) => (
                                        <div 
                                            key={index}
                                            className="strength-bar"
                                            style={{
                                                backgroundColor: index < passwordStrength.score ? getStrengthColor() : '#e0e0e0'
                                            }}
                                        ></div>
                                    ))}
                                </div>
                                <span className="strength-text" style={{ color: getStrengthColor() }}>
                                    {passwordStrength.message}
                                </span>
                            </div>
                        )}
                        
                        {newPassword && (
                            <ul className="password-requirements">
                                <li className={newPassword.length >= 8 ? "met" : ""}>
                                    <i className={newPassword.length >= 8 ? "fas fa-check" : "fas fa-times"}></i>
                                    At least 8 characters
                                </li>
                                <li className={/[A-Z]/.test(newPassword) ? "met" : ""}>
                                    <i className={/[A-Z]/.test(newPassword) ? "fas fa-check" : "fas fa-times"}></i>
                                    Uppercase letter
                                </li>
                                <li className={/[0-9]/.test(newPassword) ? "met" : ""}>
                                    <i className={/[0-9]/.test(newPassword) ? "fas fa-check" : "fas fa-times"}></i>
                                    Number
                                </li>
                                <li className={/[^A-Za-z0-9]/.test(newPassword) ? "met" : ""}>
                                    <i className={/[^A-Za-z0-9]/.test(newPassword) ? "fas fa-check" : "fas fa-times"}></i>
                                    Special character
                                </li>
                            </ul>
                        )}
                    </div>
                    
                    <div className='form-group'>
                        <label htmlFor='confirmPassword'>
                            <i className="fas fa-lock"></i> Confirm Password
                        </label>
                        <div className="password-input-container">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id='confirmPassword'
                                placeholder='Confirm new password'
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <button 
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                            </button>
                        </div>
                        {newPassword && confirmPassword && (
                            <div className="password-match">
                                {newPassword === confirmPassword ? (
                                    <span className="match-success">
                                        <i className="fas fa-check-circle"></i> Passwords match
                                    </span>
                                ) : (
                                    <span className="match-error">
                                        <i className="fas fa-exclamation-circle"></i> Passwords don't match
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <button 
                        type='submit' 
                        className={`newpassword-submit-button ${isSubmitting ? 'submitting' : ''}`}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i> Resetting Password...
                            </>
                        ) : 'Reset Password'}
                    </button>
                    
                    <div className="back-to-login">
                        <button 
                            type="button" 
                            className="back-link"
                            onClick={() => navigate('/login')}
                        >
                            <i className="fas fa-chevron-left"></i> Back to Login
                        </button>
                    </div>
                </form>
                <ToastContainer position="top-center" />
            </div>
        </div>
    );
};

export default NewPassword;